import type {
  InitialData as CustomerFormInitialData,
  ValidData as CustomerFormValidData,
} from '@/components/forms/JobEditCustomerForm.vue';
import type { VFormInstance } from '@/composables/useForm';
import type { CustomerItem } from '@/models/jobEdit';

import { ref } from 'vue';
import { isAxiosError } from 'axios';
import { type Operation, compare } from 'fast-json-patch';
import cloneDeep from 'lodash/cloneDeep';

import { useJobEditSectionBase } from '@/composables/jobEdit/useJobEditSectionBase';
import { useJobEditStore } from '@/stores/useJobEditStore';
import { fetchPutJobEditJobDetails } from '@/helpers/fetchers';
import { getApiErrorText } from '@/helpers/jobEdit';
import {
  KJobEditCustomerError,
  KJobEditUpdateUnlockedError,
} from '@/helpers/throwables';

export function useJobEditCustomer(initialCustomer: () => CustomerItem) {
  const jobEditStore = useJobEditStore();
  const formRef = ref<VFormInstance | null>(null);
  let currentFormData: CustomerFormValidData | null = null;

  function setFormRef(element: VFormInstance | null): void {
    formRef.value = element;
  }

  function setCurrentFormData(data: CustomerFormValidData): void {
    currentFormData = data;
  }

  // Only the form-editable fields: extras like customer_code would otherwise leak into the patch
  function getInitialData(): CustomerFormInitialData {
    const customer = initialCustomer();
    return {
      caller: customer.caller,
      phone: customer.phone,
      reference_1: customer.reference_1,
      reference_2: customer.reference_2,
      reference_3: customer.reference_3,
      reference_4: customer.reference_4,
    };
  }

  function buildPatchDocument(formData: CustomerFormValidData): Operation[] {
    if (!jobEditStore.originalJobData) {
      return [];
    }

    const dataToCompare = cloneDeep(jobEditStore.originalJobData);
    Object.assign(dataToCompare, formData);

    return compare(jobEditStore.originalJobData, dataToCompare);
  }

  async function saveCustomer(): Promise<boolean> {
    if (!currentFormData) {
      throw new KJobEditCustomerError('No form data available');
    }

    const patchOps = buildPatchDocument(currentFormData);

    if (patchOps.length === 0) {
      return true;
    }

    const jobId = jobEditStore.jobId;
    const jobDate = jobEditStore.jobDate;

    if (!jobId || !jobDate) {
      throw new KJobEditCustomerError('Job ID or Job Date is not available');
    }

    try {
      await fetchPutJobEditJobDetails(
        {
          job_id: jobId,
          job_date: jobDate,
        },
        patchOps,
      );

      return true;
    } catch (error) {
      if (error instanceof KJobEditUpdateUnlockedError) {
        throw error;
      }
      if (isAxiosError(error)) {
        const errorText =
          getApiErrorText(error.response?.data) ||
          error.message ||
          'Failed to save customer details';
        throw new KJobEditCustomerError(errorText);
      }
      throw error instanceof Error
        ? new KJobEditCustomerError(error.message)
        : new KJobEditCustomerError('Failed to save customer details');
    }
  }

  const sectionBase = useJobEditSectionBase({
    sectionName: 'customer-details',
    saveOperation: saveCustomer,
  });

  const initialData = ref(getInitialData());

  const cancelEditing = async (): Promise<void> => {
    initialData.value = getInitialData();
    formRef.value?.resetValidation();
    currentFormData = null;
    await sectionBase.cancelEditing();
  };

  return {
    ...sectionBase,
    cancelEditing,
    setFormRef,
    setCurrentFormData,
    initialData,
  };
}
